import { Link } from "@tanstack/react-router";
import { CircleCheck, CircleSlash, KeyRound } from "lucide-react";

import { useCan } from "@/lib/auth";

import { usePostHogClientConfig } from "../api";

/**
 * Shown when the read-back credentials are missing. Deliberately not an error
 * state: nothing is broken, it just hasn't been set up, which is why the
 * backend signals it with a 409 rather than a 5xx.
 *
 * The capture line is the important part. "Reading analytics back isn't set
 * up" reads as "analytics isn't working" — and the two are genuinely separate:
 * capture needs the public project key, reading needs a personal API key. A
 * blank Overview while events pour in looks identical to a blank Overview
 * while nothing is recorded, so the page has to say which it is.
 */
export function SetupNotice({ detail }: { detail?: string }) {
  const canManageKeys = useCan("*");
  const { data: config } = usePostHogClientConfig();
  const capturing = config?.enabled === true;

  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6">
      <div className="flex items-start gap-3">
        <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="flex flex-col gap-3">
          <div className="font-semibold">This tab can&apos;t read your data back yet</div>

          {config ? (
            <div
              className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${
                capturing ? "bg-success/10 text-success" : "bg-warning/10 text-warning"
              }`}
            >
              {capturing ? (
                <CircleCheck className="mt-0.5 h-4 w-4 shrink-0" />
              ) : (
                <CircleSlash className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <span>
                {capturing ? (
                  <>
                    <strong>Events are being captured.</strong> Usage is recording right
                    now and nothing is being lost — it just can&apos;t be displayed here
                    until the read-back credential below is added.
                  </>
                ) : (
                  <>
                    <strong>Capture is off.</strong> No usage is being recorded, so there
                    will be nothing to show here even once read-back is configured.
                  </>
                )}
              </span>
            </div>
          ) : null}

          <p className="max-w-2xl text-sm text-muted-foreground">
            {detail ??
              "Reading analytics back uses a separate read-only credential from the one that captures events."}
          </p>

          <p className="max-w-2xl text-sm text-muted-foreground">
            Create it at{" "}
            <a
              className="text-power underline underline-offset-2"
              href="https://us.posthog.com/settings/user-api-keys"
              target="_blank"
              rel="noreferrer"
            >
              us.posthog.com/settings/user-api-keys
            </a>{" "}
            with the <code className="rounded bg-muted px-1">query:read</code> scope.
            PostHog shows the value once, so copy it when it appears.
          </p>

          {canManageKeys ? (
            <Link
              to="/system/posthog"
              search={{ tab: "settings" as const }}
              className="mt-1 w-fit rounded-md bg-power px-3 py-1.5 text-sm font-semibold text-white hover:bg-power-hover"
            >
              Open Settings
            </Link>
          ) : (
            <p className="text-sm text-muted-foreground">
              A superuser can add it under Settings.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
